/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  BlobWithFileName,
  createEmptyStateResource,
  createErrorStateResource,
  createStateResource,
  doInIntervall,
  EMPTY_STRING,
  getMessageForInvalidParam,
  hasStateResourceError,
  isNotNil,
  isUnprocessableEntity,
  isValidationFieldFileSizeExceedError,
  sanitizeFileName,
  StateResource,
} from '@common/tech-shared';
import { SnackBarService } from '@ui';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { getUrl, Resource, ResourceUri } from '@ngxp/rest';
import { saveAs } from 'file-saver';
import { isNil, uniqueId } from 'lodash-es';
import { BehaviorSubject, catchError, combineLatest, first, identity, map, Observable, of, startWith } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  BinaryFileListResource,
  BinaryFileResource,
  FileUploadType,
  ScanStatus,
  ToUploadFile,
  UploadFile,
  UploadFileByIdentifier,
  UploadFilesByType,
  UploadFileStateIdentifier,
} from './binary-file.model';
import { BinaryFileRepository } from './binary-file.repository';
import { isScanPending } from './binary-file.util';

@Injectable({ providedIn: 'root' })
export class BinaryFileService {
  private readonly repository = inject(BinaryFileRepository);
  private readonly snackbarService = inject(SnackBarService);
  private readonly actions$ = inject(Actions);

  _uploadFiles$: BehaviorSubject<UploadFilesByType> = new BehaviorSubject({});

  public addFiles(type: FileUploadType, binaryFileResources: BinaryFileResource[]): void {
    if (isNil(binaryFileResources)) return;
    binaryFileResources.forEach((resource: BinaryFileResource) =>
      this._setUploadedFile(this._generateUniqueId(), type, { uploadedFile: createStateResource(resource) }),
    );
  }

  public uploadFile(toUploadFile: ToUploadFile): void {
    this.createEmptyMapIfTypeNotExists(toUploadFile.type);
    this._clearFailedUploads(toUploadFile.type);
    const uniqId: string = this._generateUniqueId();
    this._addUploadFileLoading(uniqId, toUploadFile);
    this._doUploadFile(uniqId, toUploadFile);
  }

  _clearFailedUploads(fileUploadType: FileUploadType): void {
    const uploads: UploadFileByIdentifier = this._uploadFiles$.value[fileUploadType];
    const keys: string[] = Object.keys(uploads);
    const successfulUploads: UploadFileByIdentifier = {};
    for (const key of keys) {
      if (!hasStateResourceError(uploads[key].uploadedFile)) {
        successfulUploads[key] = uploads[key];
      }
    }
    this._uploadFiles$.next({ ...this._uploadFiles$.value, [fileUploadType]: successfulUploads });
  }

  _generateUniqueId(): string {
    return uniqueId();
  }

  _addUploadFileLoading(uniqId: string, toUploadFile: ToUploadFile): void {
    this._setUploadedFile(uniqId, toUploadFile.type, {
      fileToUpload: toUploadFile.file,
      uploadedFile: createEmptyStateResource(true),
    });
  }

  _doUploadFile(uniqId: string, toUploadFile: ToUploadFile): void {
    this.repository
      .uploadFile(toUploadFile.uploadUrl, toUploadFile.file)
      .pipe(
        first(),
        map((resource: BinaryFileResource) => createStateResource(resource)),
        catchError((errorResponse: HttpErrorResponse) => this._handleError(errorResponse, uniqId, toUploadFile)),
      )
      .subscribe((stateResource: StateResource<BinaryFileResource>) =>
        this._updateUploadedFile(uniqId, toUploadFile, stateResource),
      );
  }

  _handleError(errorResponse: HttpErrorResponse, uniqId: string, toUploadFile: ToUploadFile): Observable<StateResource<unknown>> {
    this._setUploadedFile(uniqId, toUploadFile.type, {
      fileToUpload: toUploadFile.file,
      uploadedFile: createErrorStateResource(errorResponse.error),
    });
    return of(this._handleErrorByStatus(errorResponse, false));
  }

  _updateUploadedFile(
    uniqId: string,
    toUploadFile: ToUploadFile,
    binaryFileStateResource: StateResource<BinaryFileResource>,
  ): void {
    this._setUploadedFile(uniqId, toUploadFile.type, {
      fileToUpload: toUploadFile.file,
      uploadedFile: binaryFileStateResource,
    });
  }

  _setUploadedFile(uniqId: string, type: FileUploadType, uploadedFile: UploadFile): void {
    this._uploadFiles$.next({
      ...this._uploadFiles$.value,
      [type]: { ...this._uploadFiles$.value[type], [uniqId]: uploadedFile },
    });
  }

  public getUploadedFiles(type: FileUploadType): Observable<UploadFileByIdentifier> {
    this.createEmptyMapIfTypeNotExists(type);
    return this._uploadFiles$.asObservable().pipe(map((files: UploadFilesByType) => files[type]));
  }

  private createEmptyMapIfTypeNotExists(type: FileUploadType): void {
    if (!(type in this._uploadFiles$.value)) this._uploadFiles$.value[type] = {};
  }

  public isUploadInProgressForTypes(types: FileUploadType[]): Observable<boolean> {
    return combineLatest(types.map((type) => this.isUploadInProgress(type))).pipe(map((results) => results.some(identity)));
  }

  public isUploadInProgress(type: FileUploadType): Observable<boolean> {
    return this._uploadFiles$.asObservable().pipe(
      map((files: UploadFilesByType) => Object.values(files[type] || []).map((file: UploadFile) => file.uploadedFile)),
      map((files: StateResource<BinaryFileResource>[]) =>
        files.some((stateResource: StateResource<BinaryFileResource>) => stateResource.loading),
      ),
    );
  }

  public deleteUploadedFile(type: FileUploadType, key: string): void {
    const currentMap: UploadFileByIdentifier = this._uploadFiles$.value[type];
    this._uploadFiles$.next({
      ...this._uploadFiles$.value,
      [type]: Object.keys(currentMap).reduce((acc, uploadFileKey) => {
        if (uploadFileKey !== key) acc[uploadFileKey] = currentMap[uploadFileKey];
        return acc;
      }, {}),
    });
  }

  public clearUploadedFiles(type: FileUploadType): void {
    this._uploadFiles$.next({ ...this._uploadFiles$.value, [type]: {} });
  }

  _handleErrorByStatus(error: HttpErrorResponse, showValidationErrorSnackBar: boolean): StateResource<any> {
    if (isUnprocessableEntity(error.status)) {
      this._handleSnackBar(error, showValidationErrorSnackBar);
    }
    return createErrorStateResource(error.error);
  }

  _handleSnackBar(error: HttpErrorResponse, showValidationErrorSnackBar: boolean) {
    if (showValidationErrorSnackBar && isValidationFieldFileSizeExceedError(error.error)) {
      this.snackbarService.showError(getMessageForInvalidParam(EMPTY_STRING, error.error.invalidParams[0]));
    }
  }

  public downloadFile(file: BinaryFileResource, fileNamePrefix: string): Observable<StateResource<Blob>> {
    return this.repository.download(file).pipe(
      map((data) => this._saveBinaryFile(data, file, fileNamePrefix)),
      startWith(createEmptyStateResource<Blob>(true)),
      catchError(() => this._handleDownloadError()),
    );
  }

  _handleDownloadError(): Observable<StateResource<Blob>> {
    this.snackbarService.showError('Die Datei konnte nicht heruntergeladen werden.');
    return of(createEmptyStateResource<Blob>());
  }

  _saveBinaryFile(data: any, file: BinaryFileResource, fileNamePrefix: string): StateResource<Blob> {
    if (isNil(data)) {
      return createEmptyStateResource(true);
    }
    this._save(data, this.buildFileName(file, fileNamePrefix));
    return createStateResource(data);
  }

  private buildFileName(file: BinaryFileResource, fileNamePrefix: string): string {
    if (isNotNil(fileNamePrefix)) {
      return sanitizeFileName(`${fileNamePrefix}_${file.name}`);
    }
    return file.name;
  }

  public downloadArchive(uri: ResourceUri): Observable<StateResource<Blob>> {
    return this.repository.downloadArchive(uri).pipe(
      map((data: BlobWithFileName) => this._saveData(data)),
      startWith(createEmptyStateResource<Blob>(true)),
    );
  }

  _saveData(dataWithFileName: BlobWithFileName): StateResource<Blob> {
    const data: Blob = dataWithFileName.blob;
    if (isNil(data)) {
      return createEmptyStateResource(true);
    }
    this._save(data, dataWithFileName.fileName);
    return createStateResource(data);
  }

  _save(data: any, fileName: string): void {
    saveAs(data, fileName);
  }

  public getFile(uri: ResourceUri): Observable<StateResource<BinaryFileResource>> {
    return this.repository.getFile(uri).pipe(
      map((fileList: BinaryFileResource) => createStateResource(fileList)),
      startWith(createEmptyStateResource<BinaryFileResource>(true)),
    );
  }

  public getAndUpdate(
    binaryFile: BinaryFileResource,
    uploadFileStateIdentifier?: UploadFileStateIdentifier,
  ): Observable<StateResource<BinaryFileResource>> {
    return doInIntervall(this._getAndPoll(binaryFile, uploadFileStateIdentifier), this._isScanComplete);
  }

  _getAndPoll(
    binaryFile: BinaryFileResource,
    uploadFileStateIdentifier?: UploadFileStateIdentifier,
  ): Observable<StateResource<BinaryFileResource>> {
    return this.repository.getFile(getUrl(binaryFile)).pipe(
      map((binaryFile: BinaryFileResource) => createStateResource(binaryFile)),
      tap((stateResource: StateResource<BinaryFileResource>) =>
        this._updateUploadFiles(stateResource, uploadFileStateIdentifier),
      ),
    );
  }

  _updateUploadFiles(
    stateResource: StateResource<BinaryFileResource>,
    uploadFileStateIdentifier?: UploadFileStateIdentifier,
  ): void {
    if (!isNil(uploadFileStateIdentifier)) {
      const { type, key } = uploadFileStateIdentifier;
      if (this._uploadFiles$.value[type] && this._uploadFiles$.value[type][key]) {
        this._setUploadedFile(key, type, {
          ...this._uploadFiles$.value[type][key],
          uploadedFile: stateResource,
        });
      }
    }
  }

  _isScanComplete(stateResource: StateResource<BinaryFileResource>): boolean {
    return !isScanPending(stateResource.resource);
  }

  public getFiles(resource: Resource, linkRel: string): Observable<StateResource<BinaryFileListResource>> {
    return this.repository.getFiles(resource, linkRel).pipe(
      map((fileList: BinaryFileListResource) => createStateResource(fileList)),
      startWith(createEmptyStateResource<BinaryFileListResource>(true)),
    );
  }

  public hasAnyUploadedFileScanStatus(types: FileUploadType[], status: ScanStatus): Observable<boolean> {
    return combineLatest(types.map((type) => this.filterUploadedFilesByScanStatus(type, status))).pipe(
      map((results) => results.some(identity)),
    );
  }

  public isAnyUploadedFilePending(type: FileUploadType): Observable<boolean> {
    return this.filterUploadedFilesByScanStatus(type, ScanStatus.PENDING);
  }

  public isAnyUploadedFileInfected(type: FileUploadType): Observable<boolean> {
    return this.filterUploadedFilesByScanStatus(type, ScanStatus.INFECTED);
  }

  private filterUploadedFilesByScanStatus(type: FileUploadType, scanStatus: ScanStatus): Observable<boolean> {
    return this.getUploadedFiles(type).pipe(
      map((files: UploadFileByIdentifier) =>
        Object.values(files).some((file: UploadFile) => file.uploadedFile.resource?.lastScanResult?.scanStatus === scanStatus),
      ),
    );
  }
}
